import React, { useState, createContext, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import pkceChallenge from 'pkce-challenge';
import '../LandingPage.css';
import { lichessPuzzleApi, userApi } from '../api';

// Create context for auth state
const WoodpeckerAuthContext = createContext(null);

const REACT_APP_LICHESS_CLIENT_ID = 'woodpecker_lichess_client_25'

// Provider component
export function WoodpeckerAuthProvider({ children, basePath = '/woodpecker', loadPuzzleData = true }) {
  const [userEmail, setUserEmail] = useState(null);
  const [userName, setUserName] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [idToken, setIdToken] = useState(localStorage.getItem('google_id_token'));
  const [authProvider, setAuthProvider] = useState('google')
  const navigate = useNavigate();
  const [puzzleData, setPuzzleData] = useState([]);
  const [isPuzzleDataLoading, setIsPuzzleDataLoading] = useState(false);

  // Add useEffect to check for stored authentication on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('google_id_token');
    const lichessToken = localStorage.getItem('lichess_token')
    if (storedToken) {
      try {
        const decoded = jwtDecode(storedToken);
        // Check if token is expired
        if (decoded.exp * 1000 > Date.now()) {
          handleAuthAndUserData(storedToken, 'google');
        } else {
          // Token is expired, clear it
          handleLogout();
        }
      } catch (error) {
        console.error('Error processing stored token:', error);
        handleLogout();
      }
    } else if (lichessToken) {
      handleAuthAndUserData(lichessToken, 'lichess');
    }
  }, []); // Empty dependency array means this runs once on mount

  // Function to check both puzzle data and subscription status
  const checkUserData = async (token, provider, email) => {
    try {

      const [puzzleData, subscriptionData] = await Promise.all([
        fetchUserPuzzleData(token, provider, email),
        userApi.checkSubscription(token, provider, email),
        ]);
      setPuzzleData(puzzleData);

      // Update premium status
      setIsPremium(subscriptionData?.subscriptionStatus === 'active');
    } catch (error) {
      console.error('Error checking user data:', error);
    }
  };

  // Function to fetch user's puzzle data from backend
  const fetchUserPuzzleData = async (token, provider, email) => {
    const attemptFetch = async (isRetry = false) => {
      try {
        // Backend GET should now return: { items: [...], count, lastEvaluatedKey? }
        const data = await lichessPuzzleApi.getUserData(token, provider, email);
  
        if (!data || !Array.isArray(data.items)) {
          console.log("No existing puzzle sets found for user.");
          return [];
        }

        const items = [...data.items];
        // Optional client-side sort by most recently updated first
        items.sort((a, b) => (b.lastUpdated || "").localeCompare(a.lastUpdated || ""));
  
        return items;
      } catch (error) {
        console.error(`Error fetching user puzzle sets${isRetry ? ' (retry attempt)' : ''}:`, error);
  
        // Retry once on 500s
        if (!isRetry && error?.response?.status === 500) {
          console.log('Received 500 error, retrying once...');
          return attemptFetch(true);
        }
  
        return [];
      }
    };

    return attemptFetch();
  };

  // Function to handle authentication and token management
  const handleAuthentication = async (token) => {
    try {
      const decoded = jwtDecode(token);
      const email = decoded.email;
      
      setUserEmail(email);
      setUserName(decoded.name);
      setIsAuthenticated(true);
      console.log('User authenticated with email:', decoded.email);
      setIdToken(token);
      localStorage.setItem('google_id_token', token);
      
      return email;
    } catch (error) {
      console.error('Error processing authentication:', error);
      handleLogout();
      throw error;
    }
  };

  const handleLichessAuthentication = async (token) => {
    const resp = await fetch('https://lichess.org/api/account', {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!resp.ok) {
      console.log(`Lichess API error: ${resp.status}`);
      handleLogout();
    } else {
      const profile = await resp.json();
      setUserEmail(profile.username);
      setUserName(profile.username);
      setIsAuthenticated(true);
      setIdToken(token);
      localStorage.setItem('lichess_token', token);
      return profile.username;
    }
  }

  // Function to handle logout
  const handleLogout = () => {
    if (authProvider === 'lichess') {
      localStorage.removeItem('lichess_token');
    } else {
      localStorage.removeItem('google_id_token');
    }
    setIsAuthenticated(false);
    setUserEmail(null);
    setUserName(null);
    setIsPremium(false);
    setIsPuzzleDataLoading(false);
    navigate(basePath);
  };

  // Function to handle authentication and user data check
  const handleAuthAndUserData = async (token, provider='google') => {
    setIsPuzzleDataLoading(true);
    try {
      setAuthProvider(provider)
      let email;
      if (provider === 'lichess') {
        email = await handleLichessAuthentication(token)
      } else {
        email = await handleAuthentication(token);
      }
      if (loadPuzzleData) await checkUserData(token, provider, email);
    } catch (error) {
      console.error('Error in authentication flow:', error);
      handleLogout();
      navigate(basePath);
    } finally {
      setIsPuzzleDataLoading(false);
    }
  };

  const handleGoogleLoginSuccess = async (credentialResponse, rating, quantity) => {
    try {
      await handleAuthAndUserData(credentialResponse.credential);
    } catch (error) {
      console.error('Error in Google login:', error);
      handleLogout();
    }
  };

  const handleGoogleLoginError = () => {
    console.error('Google Login Failed');
    handleLogout();
    alert('Google Login Failed. Please try again.');
  };

  const handleStartSession = ({ setId, setName, userEmail, userRating, puzzleQuantity, startPuzzleIndex, initialTimeSpent, initialCycleNumber, initialSolvedCorrectly, sessionMode = 'full' }) => {
    navigate('/woodpecker/puzzle', {
      state: {
        setId,
        setName,
        startPuzzleIndex,
        initialTimeSpent,
        initialCycleNumber,
        initialSolvedCorrectly,
        sessionMode,
        userEmail, 
        userRating, 
        numPuzzles: puzzleQuantity
      }
    });
  };

  const startLichessOAuth = async () => {
    sessionStorage.setItem('lichess_return_to', window.location.pathname + window.location.search);
    const { code_challenge, code_verifier } = await pkceChallenge();
    localStorage.setItem('pkce_verifier', code_verifier);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: REACT_APP_LICHESS_CLIENT_ID,
      redirect_uri: `${window.location.origin}/woodpecker/oauth/callback`,
      scope: 'puzzle:read',
      code_challenge,
      code_challenge_method: 'S256',
      state: crypto.randomUUID(),
    });
    window.location.href = `https://oauth.lichess.org/oauth?${params}`;
  }

  const value = {
    idToken,
    authProvider,
    userEmail,
    userName,
    isAuthenticated,
    isPremium,
    isPuzzleDataLoading,
    puzzleData,
    setPuzzleData,
    handleAuthAndUserData,
    startLichessOAuth,
    handleGoogleLoginError,
    handleStartSession,
    handleLogout,
    handleGoogleLoginSuccess
  };

  return (
    <WoodpeckerAuthContext.Provider value={value}>
      {children}
    </WoodpeckerAuthContext.Provider>
  );
}

// Custom hook to use the auth context
export function useWoodpeckerAuth() {
  const context = useContext(WoodpeckerAuthContext);
  if (!context) {
    throw new Error('useWoodpeckerAuth must be used within a WoodpeckerAuthProvider');
  }
  return context;
}
